<script>
  import NavbarUnauthenticated from "#lib/NavbarUnauthenticated.svelte";
  import Navbar from "#lib/Navbar.svelte";
  import PricingPlanCard from "#lib/PricingPlanCard.svelte";
  import Logo from "@local/assets/icons/se-logo.svg";
  import CheckIcon from "@local/assets/icons/check.svg";
  import ShapeXAiLogo from "@local/assets/icons/shpx-ai-logo.svg";
  import LockIcon from "@local/assets/icons/lock.svg";
  import { createStripeCheckoutSession } from "#src/api/stripe.js";
  import isLoggedIn from "#src/extensions/is-logged-in.js";
  import { user, profile } from "#src/stores/auth.js";
  import { alerts } from "#src/stores/alerts";
  import { UX_MODE } from "#src/env.js";

  export let showUpgrade = false;
  export let queryParams = {};

  $: selectedTier = queryParams?.tier ?? null;
  $: showTier = (tier) => !selectedTier || selectedTier === tier;

  // Single-tier checkout view: a focused, marketing-matched card shown when a
  // specific plan is pre-selected (e.g. app.shpx.ai/pricing?tier=pro), instead
  // of the full plan-comparison grid below.
  const checkoutTiers = {
    pro: {
      label: "Pro",
      price: "$95",
      unit: "user",
      badge: true,
      sell: "Use the AI tools on your own or with your team.",
      checks: [
        "AI analyzer converts an entire elevation sheet into framed units in seconds",
        "Unlimited self-service AI use (reasonable-use policy)",
        "Try ShapeX Assist on up to two sheets",
      ],
      priceId: "price_1THT0YKCaJ5iM96w6QmmbTLE",
      subscriptionType: "pro",
    },
    team_starter: {
      label: "Team Starter",
      price: "$395",
      unit: "team",
      badge: false,
      sell: "For small teams. AI tools plus the ShapeX Assist service.",
      checks: [
        "Everything in Pro",
        "ShapeX Assist: we process up to 30 sheets per month",
        "First month: unlimited users so the whole team can try it",
        "After that: 2 users included",
      ],
      priceId: "price_1TNDlFKCaJ5iM96wxBM93PxZ",
      subscriptionType: "team_starter",
    },
    team_pro: {
      label: "Team Pro",
      price: "$995",
      unit: "team",
      badge: false,
      sell: "For larger teams or higher project volume.",
      checks: [
        "Everything in Team Starter",
        "ShapeX Assist: unlimited sheets processed per month",
        "Unlimited users",
        "No pre-set limits on the AI or the service",
      ],
      priceId: "price_1TJHQvKCaJ5iM96wRYQFYTjw",
      subscriptionType: "team_pro",
    },
  };

  $: isCheckout = UX_MODE === "shpx" && !!checkoutTiers[selectedTier];
  $: checkoutTier = isCheckout ? checkoutTiers[selectedTier] : null;

  async function subscribe(priceId) {
    const session = await createStripeCheckoutSession({
      price: priceId,
      quantity: 1,
      success_url: "onboard-success",
      cancel_url: "onboard-cancel",
      consent_collection: {
        terms_of_service: "required",
      },
      allow_promotion_codes: true,
      metadata: {
        onboard_type: "self-onboard",
        create_user_account: "true",
      },
    });
    if (session.url) {
      window.location.href = session.url;
    } else {
      alerts.add({ type: "error", message: "Error creating checkout session." });
      console.log(session);
    }
  }
</script>

{#if isCheckout}
<div class="h-full w-full flex flex-col overflow-y-auto" style="font-family: 'Segoe UI', Arial, sans-serif;">
  <div class="h-16 flex items-center justify-between px-6 bg-[#0b1420] flex-none">
    <div class="flex items-center">
      <ShapeXAiLogo />
    </div>
    <div class="flex items-center gap-1.5 text-slate-400 text-xs">
      <LockIcon />
      Secure checkout
    </div>
  </div>

  <div class="grow flex flex-col items-center justify-center bg-[#f5f8fd] px-5 py-11">
    <span
      class="inline-block text-xs font-bold tracking-wide uppercase text-[#3B7DD8] bg-[#eaf2fd] px-3 py-1.5 rounded-full mb-4">
      One step to go
    </span>

    <PricingPlanCard
      tier={checkoutTier}
      ctaLabel={`Subscribe to ${checkoutTier.label}`}
      highlight
      showBadge={false}
      {showUpgrade}
      currentType={$profile?.organization?.shpx_subscription_type}
      onSubscribe={subscribe} />

    <p class="text-center mt-[1.625rem] text-[0.9rem] text-[#4a5568]">
      <span class="text-[#C8890A] font-extrabold mr-1.5">&#10003;</span>Every plan is month to month. Cancel anytime.
    </p>

    <a href="https://shpx.ai/#pricing" class="text-[#3B7DD8] font-semibold text-sm hover:underline mt-3">
      &larr; Choose a different plan
    </a>
  </div>
</div>
{:else}
  {#if isLoggedIn($user)}
    <Navbar />
  {:else}
    <NavbarUnauthenticated />
  {/if}
  <div class="grow flex items-center bg-gray-200">
    <div class="w-full">
      {#if showUpgrade}
        <div class="text-lg px-8 py-4">Upgrade for additional features:</div>
      {:else if !selectedTier}
        <div class="text-lg px-8 py-4">Choose the plan that's right for you:</div>
      {/if}
      <div class="flex justify-center py-4">
        <div class="flex flex-col md:flex-row gap-4 items-stretch">
          {#if UX_MODE === "shpx"}
            {#each Object.entries(checkoutTiers) as [key, tier] (key)}
              {#if showTier(key)}
                <PricingPlanCard
                  {tier}
                  ctaLabel="Subscribe"
                  highlight={tier.badge}
                  {showUpgrade}
                  currentType={$profile?.organization?.shpx_subscription_type}
                  onSubscribe={subscribe} />
              {/if}
            {/each}
          {:else}
            {#if showTier("shpx-glaz")}
              <div class="p-8 w-72 flex flex-col justify-between gap-8 bg-white rounded-lg">
                <div class="space-y-4">
                  <div>
                    <Logo />
                  </div>
                  <div class="text-lg font-bold leading-none mb-4" style="min-height: 2.5rem;">
                    Shape Exchange for Glaziers
                  </div>
                  <div>
                    <div class="text-gray-400 text-xs">Starting at</div>
                    <div class="flex items-center gap-1">
                      <div class="text-3xl font-bold">$390</div>
                      <div class="text-xs text-gray-400 leading-none">per<br />month</div>
                    </div>
                    <div class="text-xs text-gray-400 leading-none">for two users</div>
                  </div>
                </div>
                <div class="space-y-4">
                  <a href="https://calendly.com/shape-exchange/intro" target="_blank">
                    <button class="btn btn-primary-alt w-full btn-lg">
                      <div class="text-base">Book a Demo</div>
                    </button>
                  </a>

                  <div class="text-xs space-y-2 pl-4">
                    <div>Additional features:</div>
                    <ul class="space-y-1">
                      <li class="flex gap-4 items-center"><CheckIcon /> WinBidPro/PartnerPak Export</li>
                      <li class="flex gap-4 items-center"><CheckIcon /> Tagging of Openings</li>
                      <li class="flex gap-4 items-center"><CheckIcon /> Multiplexed Tallies</li>
                      <li class="flex gap-4 items-center"><CheckIcon /> Glass Type Editor</li>
                    </ul>
                  </div>
                </div>
                {#if selectedTier}
                  <a
                    href="https://shpx.ai/#pricing"
                    class="text-blue-600 hover:underline text-sm flex items-center gap-1">
                    <span aria-hidden="true">←</span> Choose a different plan
                  </a>
                {/if}
              </div>
            {/if}

            {#if showTier("shpx-fab")}
              <div class="p-8 w-72 flex flex-col justify-between gap-8 bg-white rounded-lg">
                <div class="space-y-4">
                  <div>
                    <Logo />
                  </div>
                  <div class="text-lg font-bold leading-none mb-4" style="min-height: 2.5rem;">
                    Shape Exchange for Fabricators
                  </div>
                  <div>
                    <div class="text-gray-400 text-xs">Contact us for pricing</div>
                    <div class="flex items-center gap-1"></div>
                  </div>
                </div>
                <div class="space-y-4">
                  <a href="https://calendly.com/shape-exchange/intro" target="_blank">
                    <button class="btn btn-primary-alt w-full btn-lg">
                      <div class="text-base">Book a Demo</div>
                    </button>
                  </a>

                  <div class="text-xs space-y-2 pl-4">
                    <div>Additional features:</div>
                    <ul class="space-y-1">
                      <li class="flex gap-4 items-center"><CheckIcon /> Quick Quoting UX</li>
                      <li class="flex gap-4 items-center"><CheckIcon /> Product Library</li>
                      <li class="flex gap-4 items-center"><CheckIcon /> CRM Integration</li>
                      <li class="flex gap-4 items-center"><CheckIcon /> Import Templates</li>
                    </ul>
                  </div>
                </div>
                {#if selectedTier}
                  <a
                    href="https://shpx.ai/#pricing"
                    class="text-blue-600 hover:underline text-sm flex items-center gap-1">
                    <span aria-hidden="true">←</span> Choose a different plan
                  </a>
                {/if}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
